import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true, email: data.email };
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      const status = e.response?.status;
      return {
        success: false,
        locked: status === 423,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  const verify2FA = async (email, code) => {
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { email, code }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resend2FA = async (email) => {
    try {
      await axios.post(`${API}/auth/2fa/resend`, { email }, { withCredentials: true });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      await checkAuth();
    } catch {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2FA, resend2FA, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}
